import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { AlertTriangle, ArrowUpRight } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "sonner";
import { Button } from "@/components/catalyst/Button";
import { Heading } from "@/components/catalyst/Heading";
import { Text } from "@/components/catalyst/Text";
import { useCanCreate, useSubscription } from "@/hooks/use-subscription";
import { orpc } from "@/orpc/client";
import type { CreateReferralLinkFormValues } from "../schema";
import { createReferralLinkSchema } from "../schema";
import { slideTransition, slideVariants } from "./animations";
import { StepFinancialTerms } from "./StepFinancialTerms";
import { StepIndicator } from "./StepIndicator";
import { StepPartnerAndCode } from "./StepPartnerAndCode";
import { StepReview } from "./StepReview";

type Step = 0 | 1 | 2;

const STEPS = [
	{ label: "Partner & Code" },
	{ label: "Financial Terms" },
	{ label: "Review & Confirm" },
];

/** Fields to validate when moving forward from each step. */
const STEP_FIELDS: (keyof CreateReferralLinkFormValues)[][] = [
	["partnerId", "slug"],
	["payoutRateBps", "minPayoutDollars"],
	[],
];

/** Full-page wizard for creating a referral link. */
export function CreateReferralLinkWizard() {
	const { appId } = useParams({
		from: "/_app/apps/$appId/links/create",
	});
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const {
		canCreate,
		usage,
		limit,
		isLoading: isLoadingLimits,
	} = useCanCreate("links");
	const { data: subscription } = useSubscription();
	const defaultMinPayoutDollars =
		(subscription?.limits.minPayoutThresholdCents ?? 2500) / 100;

	const [step, setStep] = useState<Step>(0);
	const [direction, setDirection] = useState<1 | -1>(1);

	const methods = useForm<CreateReferralLinkFormValues>({
		resolver: zodResolver(createReferralLinkSchema),
		defaultValues: {
			partnerId: "",
			slug: "",
			planHint: null,
			payoutRateBps: "" as unknown as number,
			minPayoutDollars: defaultMinPayoutDollars,
			payoutDurationDays: null,
			maxRedemptions: null,
			expiresAt: null,
		},
	});

	const createMutation = useMutation(
		orpc.referralLinks.create.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: orpc.referralLinks.list.key(),
				});
				queryClient.invalidateQueries({
					queryKey: orpc.billing.getSubscription.key(),
				});
				toast.success(
					"Referral link created. An approval email has been sent to the partner.",
				);
				navigate({
					to: "/apps/$appId",
					params: { appId },
					search: { tab: "referral_links" },
				});
			},
			onError: (error) => {
				toast.error(error.message);
			},
		}),
	);

	const goNext = async () => {
		const fields = STEP_FIELDS[step];
		if (fields.length > 0) {
			const isValid = await methods.trigger(fields);
			if (!isValid) return;
		}
		setDirection(1);
		setStep((s) => Math.min(s + 1, 2) as Step);
	};

	const goBack = () => {
		setDirection(-1);
		setStep((s) => Math.max(s - 1, 0) as Step);
	};

	const onSubmit = (data: CreateReferralLinkFormValues) => {
		let expiresAt: Date | null = null;
		if (data.expiresAt) {
			expiresAt = new Date(data.expiresAt);
		}

		const { payoutRateBps, minPayoutDollars, ...rest } = data;
		const actualPayoutRateBps = Math.round(Number(payoutRateBps) * 100);
		const minPayoutCents = Math.round(Number(minPayoutDollars) * 100);

		createMutation.mutate({
			appId,
			...rest,
			payoutRateBps: actualPayoutRateBps,
			minPayoutCents,
			maxRedemptions: data.maxRedemptions ?? null,
			payoutDurationDays: data.payoutDurationDays ?? null,
			expiresAt,
		});
	};

	const navigateBack = () => {
		navigate({
			to: "/apps/$appId",
			params: { appId },
			search: { tab: "referral_links" },
		});
	};

	// Subscription limit reached
	if (!isLoadingLimits && !canCreate) {
		return (
			<div className="mx-auto max-w-2xl py-12">
				<div className="text-center">
					<div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-warning/10">
						<AlertTriangle className="size-6 text-warning" />
					</div>
					<Heading level={2}>Link Limit Reached</Heading>
					<Text className="mt-2">
						You've used{" "}
						<strong>
							{usage}/{limit === Number.POSITIVE_INFINITY ? "∞" : limit}
						</strong>{" "}
						links on your current plan.
					</Text>
					<Text className="mt-1 text-base-content/60">
						Upgrade your plan to create more referral links.
					</Text>
					<div className="mt-6 flex items-center justify-center gap-3">
						<Button plain onClick={navigateBack}>
							Back
						</Button>
						<Button
							onClick={() =>
								navigate({
									to: "/settings",
									search: { tab: "billing" },
								})
							}
						>
							<ArrowUpRight className="size-4" />
							Upgrade Plan
						</Button>
					</div>
				</div>
			</div>
		);
	}

	const stepComponents = [
		<StepPartnerAndCode key="step-0" />,
		<StepFinancialTerms key="step-1" />,
		<StepReview key="step-2" />,
	];

	return (
		<FormProvider {...methods}>
			<div className="max-w-2xl py-8">
				{/* Step indicator */}
				<StepIndicator steps={STEPS} currentStep={step} />

				{/* Animated step content */}
				<div className="relative overflow-hidden">
					<AnimatePresence mode="wait" custom={direction}>
						<motion.div
							key={step}
							custom={direction}
							variants={slideVariants}
							initial="enter"
							animate="center"
							exit="exit"
							transition={slideTransition}
						>
							{stepComponents[step]}
						</motion.div>
					</AnimatePresence>
				</div>

				{/* Navigation buttons */}
				<div className="mt-8 flex items-center justify-between border-t border-base-content/10 pt-6">
					{step > 0 ? (
						<Button outline onClick={goBack}>
							Back
						</Button>
					) : (
						<Button plain onClick={navigateBack}>
							Cancel
						</Button>
					)}

					{step < 2 ? (
						<Button onClick={goNext}>Continue</Button>
					) : (
						<Button
							color="indigo"
							onClick={methods.handleSubmit(onSubmit)}
							disabled={createMutation.isPending}
						>
							{createMutation.isPending
								? "Creating..."
								: "Create & Notify Partner"}
						</Button>
					)}
				</div>
			</div>
		</FormProvider>
	);
}
